import type {
  Address,
  CarrierCatalogueEntry,
  OrganisationRoute,
  RouteCatalogueEntry,
} from '@my/directory-api';

/** Generic Map indexer — O(1) key→entry lookup. */
export function indexEntries<T, K>(items: readonly T[], getKey: (item: T) => K): Map<K, T> {
  const map = new Map<K, T>();
  for (const item of items) map.set(getKey(item), item);
  return map;
}

/**
 * Resolve the org's `OrganisationRoute` rows against the catalogue, dropping
 * keys that don't match (defensive — keys are append-only by convention).
 * Sorted by origin port name then destination.
 */
export function resolveOrganisationRoutes<T extends RouteCatalogueEntry>(
  rows: readonly OrganisationRoute[],
  catalogueByKey: Map<string, T>,
): Array<{ row: OrganisationRoute; entry: T }> {
  const out: Array<{ row: OrganisationRoute; entry: T }> = [];
  for (const row of rows) {
    const entry = catalogueByKey.get(row.catalogueKey);
    if (!entry) continue;
    out.push({ row, entry });
  }
  out.sort(
    (a, b) =>
      a.entry.originPortName.localeCompare(b.entry.originPortName) ||
      a.entry.destinationPortName.localeCompare(b.entry.destinationPortName),
  );
  return out;
}

/**
 * Suffix marking the reverse leg of a route in combobox values. A catalogue
 * FILE entry is stored in one direction but sailed both ways, so the picker
 * offers both legs; a My.Ferry route is one direction and gets no reverse.
 * Goes with the file once the picks saved under its keys are remapped.
 */
const ROUTE_REVERSE_SUFFIX = ':reverse';

export function bookingRouteOptionValue(catalogueKey: string, reversed: boolean): string {
  return reversed ? `${catalogueKey}${ROUTE_REVERSE_SUFFIX}` : catalogueKey;
}

export function parseBookingRouteOptionValue(value: string): {
  catalogueKey: string;
  reversed: boolean;
} {
  if (value.endsWith(ROUTE_REVERSE_SUFFIX)) {
    return {
      catalogueKey: value.slice(0, -ROUTE_REVERSE_SUFFIX.length),
      reversed: true,
    };
  }
  return { catalogueKey: value, reversed: false };
}

/**
 * Combobox label, reversible. Forward: `Dublin → Holyhead · Irish Ferries (IE → GB)`.
 * Reverse swaps port + country pair; carrier stays the same.
 */
export function bookingRouteOptionLabel(
  entry: RouteCatalogueEntry,
  carrier: CarrierCatalogueEntry | null | undefined,
  reversed: boolean = false,
): string {
  const origin = reversed ? entry.destinationPortName : entry.originPortName;
  const destination = reversed ? entry.originPortName : entry.destinationPortName;
  const originCountry = reversed ? entry.destinationCountryIsoCode : entry.originCountryIsoCode;
  const destinationCountry = reversed
    ? entry.originCountryIsoCode
    : entry.destinationCountryIsoCode;
  const carrierLabel = carrier?.name ?? entry.carrierKey;
  return `${origin} → ${destination} · ${carrierLabel} (${originCountry} → ${destinationCountry})`;
}

/**
 * Per-carrier terminal address code: `"{carrierCode}-{portCode}"` —
 * `"STENA-IEDUB"` from a My.Ferry route (its carrier code and the port's
 * UN/LOCODE), `"IF-DUB"` from a catalogue-file entry (the file's own codes).
 * Returns null when either half is missing so callers can fall back to
 * the legacy name-only match/create path. Upper-cased + trimmed so the
 * code is stable regardless of how the data was entered.
 */
export function buildPortAddressCode(
  carrierCode: string | null | undefined,
  portCode: string | null | undefined,
): string | null {
  const carrier = carrierCode?.trim().toUpperCase();
  const port = portCode?.trim().toUpperCase();
  if (!carrier || !port) return null;
  return `${carrier}-${port}`;
}

/**
 * Match an existing address by its terminal `code` (case-insensitive).
 * This is the primary lookup for port terminals: matching by code rather
 * than name keeps two carriers' terminals at the same physical port
 * (e.g. `IF-DUB` vs `SL-DUB`, both "Dublin Port") as distinct rows.
 * Returns null when no address carries that code (legacy name-only rows
 * have no code, so they never match here — a fresh coded row is created).
 */
export function matchAddressByCode(
  addresses: readonly Address[],
  code: string,
): Address | null {
  if (!code) return null;
  const target = code.trim().toUpperCase();
  for (const a of addresses) {
    if ((a.code ?? '').trim().toUpperCase() === target) return a;
  }
  return null;
}

/**
 * Best-effort match of a route's port (name + ISO country) to an existing
 * address. Exact name+country → case-insensitive name+country →
 * case-insensitive name anywhere. Returns null when nothing matches.
 */
export function matchAddressForPort(
  addresses: readonly Address[],
  portName: string,
  countryIsoCode: string,
): Address | null {
  if (!portName) return null;
  const lowerName = portName.toLowerCase();
  const upperCountry = countryIsoCode.toUpperCase();
  for (const a of addresses) {
    if (a.name === portName && (a.countryIsoCode ?? '').toUpperCase() === upperCountry) {
      return a;
    }
  }
  for (const a of addresses) {
    if (
      a.name.toLowerCase() === lowerName &&
      (a.countryIsoCode ?? '').toUpperCase() === upperCountry
    ) {
      return a;
    }
  }
  for (const a of addresses) {
    if (a.name.toLowerCase() === lowerName) return a;
  }
  return null;
}
